import { useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { api, JourneyCompareFunnel, JourneyEasycartAlign } from "@/lib/api";
import {
  JOURNEY_WINDOW_OPTIONS,
  JourneyWindowId,
  journeyWindowParams,
} from "@/lib/journeyWindow";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { InfoTip, LabelWithTip } from "@/components/ui/tooltip";
import DailyTrendChart from "@/components/DailyTrendChart";
import StatsExplainer from "@/components/StatsExplainer";
import JourneyEntryGroupsSplit from "@/components/JourneyEntryGroupsSplit";
import { cn, formatPLN } from "@/lib/utils";
import type { MetricTipId } from "@/lib/metricsGlossary";

const STEP_META: Record<string, { label: string; tipId: MetricTipId }> = {
  view: { label: "Widok produktu", tipId: "funnel_view" },
  checkout: { label: "Klik „Kupuję”", tipId: "funnel_buy_click" },
  cart: { label: "Koszyk", tipId: "funnel_cart" },
  purchase: { label: "Zakup", tipId: "funnel_purchase_ec" },
};

function pct(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return "-";
  return `${Math.round(v * 1000) / 10}%`;
}

function biggestDropInsight(funnels: JourneyCompareFunnel[]): string {
  const ready = funnels.filter((f) => f.enough_data && f.drops?.length);
  if (!ready.length) {
    return "Za mało wizyt na produkt (min. 15), żeby porównać ucieczki między koszykami.";
  }
  let best: { label: string; from: string; to: string; pct: number; n: number } | null = null;
  let bestPurchase: { label: string; rate: number } | null = null;

  for (const f of ready) {
    const purchase = f.steps.find((s) => s.id === "purchase");
    const entry = f.steps[0];
    if (purchase && entry && entry.n > 0) {
      const rate = purchase.n / Math.max(entry.n, 1);
      if (!bestPurchase || rate > bestPurchase.rate) {
        bestPurchase = { label: f.label, rate };
      }
    }
    for (const d of f.drops) {
      if (!best || d.pct > best.pct) {
        best = { label: f.label, from: d.from, to: d.to, pct: d.pct, n: d.n };
      }
    }
  }
  if (!best) return "";
  const fromL = STEP_META[best.from]?.label ?? best.from;
  const toL = STEP_META[best.to]?.label ?? best.to;
  let line = `Największa ucieczka: ${best.label} na ${fromL} → ${toL} (−${pct(best.pct)}, ${best.n} wizyt).`;
  if (bestPurchase) {
    line += ` Najlepszy udział do zakupu: ${bestPurchase.label} (${pct(bestPurchase.rate)}).`;
  }
  return line;
}

function EasyCartAlignBanner({ align }: { align?: JourneyEasycartAlign }) {
  if (!align) return null;
  const j = align.journey;
  const e = align.easycart;
  const orders = e?.orders ?? [];
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">
          Sprzedaż (EasyCart) vs ruch na stronie (nasz licznik)
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Pieniądze i zakupy: EasyCart. Wejścia do koszyka: nasz licznik na stronie. Dzień = czas
          polski, jak w panelu EasyCart. Anulowane widać na liście, poza sumą.
        </p>
      </CardHeader>
      <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Metric
          label={<LabelWithTip tipId="cart_sessions_ours" />}
          value={String(j?.cart_sessions ?? 0)}
        />
        <div>
          <div className="text-xs text-muted-foreground">
            <LabelWithTip tipId="purchases_easycart" />
          </div>
          <div className="text-lg font-semibold tabular-nums">{e?.paid_orders ?? 0}</div>
          <div className="text-[11px] text-muted-foreground">
            z tego widoczne na ścieżce: {j?.purchase_sessions ?? 0}
            <InfoTip tipId="purchases_on_path" className="ml-1 inline-flex align-middle" />
            {(e?.cancelled_orders ?? 0) > 0
              ? ` · +${e?.cancelled_orders} anulowanych (poza sumą)`
              : ""}
          </div>
        </div>
        <Metric
          label={<LabelWithTip tipId="revenue_easycart" />}
          value={formatPLN(e?.revenue ?? 0)}
          hint={`Średnio ${formatPLN(e?.aov ?? 0)} na zamówienie`}
        />
        <Metric
          label={<LabelWithTip tipId="purchase_coverage" />}
          value={pct(align.coverage?.purchase_vs_easycart)}
        />
      </CardContent>
      {orders.length > 0 && (
        <div className="border-t border-border/50 px-6 py-3">
          <div className="mb-2 text-xs font-medium text-muted-foreground">
            Zamówienia w okresie (czas PL jak panel EasyCart)
          </div>
          <ul className="grid gap-1.5 text-xs">
            {orders.map((o) => (
              <li
                key={`${o.order_ref}-${o.ordered_at}`}
                className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5"
              >
                <span className="font-mono tabular-nums text-muted-foreground">
                  {o.ordered_at_pl || o.ordered_at}
                </span>
                <span className="font-mono">#{o.order_ref}</span>
                <span>{o.payment_status}</span>
                <span className="text-muted-foreground">
                  {(o.product_name || "").slice(0, 42)}
                  {(o.product_name || "").length > 42 ? "…" : ""}
                </span>
                <span className="tabular-nums">
                  {o.in_kpi ? formatPLN(o.revenue ?? 0) : "poza sumą"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <p className="border-t border-border/50 px-6 py-3 text-xs text-muted-foreground">
        Panel EasyCart pokazuje własne „Sesje zakupowe”, których nie da się pobrać przez API.
        Dlatego ta liczba u nas nie występuje i nie porównuj jej 1:1 z naszymi wejściami do koszyka.
        <InfoTip tipId="easycart_shopping_sessions_note" className="ml-1 inline-flex align-middle" />
      </p>
    </Card>
  );
}

function Metric({
  label,
  value,
  hint,
}: {
  label: ReactNode;
  value: string;
  hint?: string;
}) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold tabular-nums">{value}</div>
      {hint && <div className="text-[11px] text-muted-foreground">{hint}</div>}
    </div>
  );
}

export default function JourneyProductFunnels() {
  const [period, setPeriod] = useState<JourneyWindowId>("7");

  const query = useQuery({
    queryKey: ["site-analytics-journey-products", period],
    queryFn: () => api.siteAnalyticsJourney(journeyWindowParams(period)),
    retry: false,
  });

  const products = query.data?.product_funnels?.products ?? [];
  const align = query.data?.easycart_align;
  const daily = query.data?.daily?.days ?? [];
  const insight = useMemo(() => biggestDropInsight(products), [products]);

  return (
    <div className="grid gap-5">
      <StatsExplainer variant="koszyki" />

      <div className="flex flex-wrap items-center gap-2">
        {JOURNEY_WINDOW_OPTIONS.map((opt) => (
          <Button
            key={opt.id}
            size="sm"
            variant={opt.id === period ? "default" : "outline"}
            onClick={() => setPeriod(opt.id)}
          >
            {opt.label}
          </Button>
        ))}
        <span className="ml-2 text-xs text-muted-foreground">
          {query.data?.period?.label_pl ?? "czas PL"}
          {" · "}
          Lejek: widok → Kupuję → koszyk (nasz licznik) → zakup (tylko EasyCart)
        </span>
      </div>

      {query.data?.ok && (
        <JourneyEntryGroupsSplit channels={query.data.acquisition_channels} />
      )}

      {query.data && <EasyCartAlignBanner align={align} />}

      {query.data && (
        <DailyTrendChart
          title="Koszyk vs zakupy dzień po dniu"
          subtitle="Niebieski: wejścia do koszyka (nasz licznik). Zielony: opłacone zamówienia EasyCart."
          data={daily.map((d) => ({
            date: d.date,
            cart: d.cart_sessions ?? 0,
            paid: d.easycart_paid_orders ?? 0,
          }))}
          series={[
            { key: "cart", label: "Wejścia do koszyka", color: "hsl(var(--primary))" },
            { key: "paid", label: "Zakupy EasyCart", color: "hsl(142 60% 40%)", rightAxis: true },
          ]}
          mode="bar"
        />
      )}

      {query.isLoading && (
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">
            Wczytywanie lejków produktów…
          </CardContent>
        </Card>
      )}
      {query.isError && (
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">
            Nie udało się wczytać danych.
          </CardContent>
        </Card>
      )}

      {query.data && products.length === 0 && (
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">
            Brak zdarzeń produktowych w tym okresie.
          </CardContent>
        </Card>
      )}

      {products.length > 0 && (
        <div className="overflow-x-auto pb-2">
          <div
            className="grid gap-4"
            style={{
              gridTemplateColumns: `repeat(${Math.min(products.length, 6)}, minmax(200px, 1fr))`,
              minWidth: `${Math.min(products.length, 6) * 200}px`,
            }}
          >
            {products.map((f, i) => (
              <ProductFunnelColumn key={f.key} funnel={f} index={i} />
            ))}
          </div>
        </div>
      )}

      {insight && (
        <p className="rounded-lg border border-border/60 bg-muted/30 px-4 py-3 text-sm leading-relaxed">
          {insight}
          <InfoTip tipId="biggest_drop" className="ml-1 inline-flex align-middle" />
        </p>
      )}
    </div>
  );
}

function ProductFunnelColumn({
  funnel,
  index,
}: {
  funnel: JourneyCompareFunnel;
  index: number;
}) {
  const ec = funnel.easycart;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04 }}
    >
      <Card className="h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-base leading-snug">{funnel.label}</CardTitle>
          <p className="text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              {funnel.sessions} wizyt z tym produktem
              <InfoTip tipId="product_visits" />
            </span>
          </p>
          {ec && (ec.paid_orders || 0) > 0 && (
            <p className="text-[11px] text-muted-foreground">
              EasyCart: {ec.paid_orders} opłaconych · {formatPLN(ec.revenue ?? 0)}
            </p>
          )}
        </CardHeader>
        <CardContent>
          {funnel.steps.length === 0 ? (
            <p className="py-6 text-sm text-muted-foreground">Brak kroków w lejku.</p>
          ) : (
            <div className="flex flex-col items-center gap-1">
              {!funnel.enough_data && (
                <p className="mb-2 w-full text-[11px] leading-snug text-muted-foreground">
                  Mała próbka (poniżej 15 wizyt). Liczby widoczne, wnioski ostrożne.
                  <InfoTip tipId="small_sample" className="ml-1 inline-flex align-middle" />
                </p>
              )}
              {funnel.steps.map((step, i) => {
                const drop = funnel.drops[i];
                const widthPct = Math.max(8, Math.round((step.pct_of_entry || 0) * 100));
                const meta = STEP_META[step.id];
                return (
                  <div key={step.id} className="flex w-full flex-col items-center">
                    <div
                      className={cn(
                        "rounded-md px-2 py-2 text-center text-primary-foreground shadow-sm",
                        step.id === "purchase" ? "bg-emerald-600/85" : "bg-primary/85",
                      )}
                      style={{ width: `${widthPct}%`, minWidth: "4.5rem" }}
                    >
                      <div className="flex items-center justify-center gap-0.5 text-[11px] font-medium opacity-90">
                        {meta?.label ?? step.id}
                        {meta && (
                          <InfoTip tipId={meta.tipId} className="text-primary-foreground/80" />
                        )}
                      </div>
                      <div className="text-sm font-semibold tabular-nums">{step.n}</div>
                      <div className="text-[10px] opacity-80">
                        {pct(step.pct_of_entry)}
                        {i > 0 ? ` · ${pct(step.pct_of_prev)} z poprz.` : ""}
                      </div>
                    </div>
                    {drop && (
                      <div className="my-1 text-[11px] text-muted-foreground">
                        ↓ −{drop.n} ({pct(drop.pct)})
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <ProductLandingsBlock landings={funnel.landings} />
        </CardContent>
      </Card>
    </motion.div>
  );
}

function ProductLandingsBlock({
  landings,
}: {
  landings?: JourneyCompareFunnel["landings"];
}) {
  if (!landings?.length) return null;
  return (
    <div className="mt-4 border-t border-border/50 pt-3">
      <p className="mb-2 flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        Z których stron do tego koszyka
        <InfoTip tipId="landings_to_cart" />
      </p>
      <ul className="space-y-1.5">
        {landings.map((row) => (
          <li
            key={`${row.page_id}:${row.entry_page}`}
            className="text-[11px] leading-snug text-muted-foreground"
          >
            <span className="font-medium text-foreground">{row.label}</span>
            <span className="tabular-nums">
              {" · "}
              {row.sessions} wizyt
              {" · "}
              {row.reached_cart} do koszyka
              {" · "}
              {pct(row.cart_rate)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
